import type {
  UpAction,
  UpActionData,
  UpActionTopic,
  UpMessage,
  UpRequest,
  UpRequestData,
  UpTopic,
} from '@/common/interop/up-messages';
import type {
  IframeCloseResponse,
  IframeResponse,
} from '@/common/interop/iframe';
import type { RoundTripTopic } from '@/common/interop/round-trip';
import type { DownResponseData } from '@/common/interop/down-messages';

import { Envelope, isErrorEnvelope } from '@/common/interop/envelope';

import { ConnectionError, Logger } from '../types';
import { EventEmitter } from '../common/event-emitter';
import {
  closeWebSocket,
  connectWebSocket,
  sendWebSocketMessage,
} from './iframe';

export function createEventId(topic: RoundTripTopic) {
  return `${topic}_${crypto.randomUUID()}`;
}

export class AgentWebSocket extends EventEmitter {
  private hasAddedListeners = false;

  private connected = false;
  private neverBeenConnected = true;
  private connectAttempts = 1;

  private autoReconnect = true;
  private reconnectTimer: number | undefined;

  private pendingEventIds = new Set<string>();

  constructor(
    private port: MessagePort,
    private logger: Logger,
    /** Max reconnect intterval in milliseconds. */
    private maxReconnectInterval: number = 60000,
    /** Max number of reconnects before giving up.*/
    private maxReconnectAttempts: number = 77,
  ) {
    super();
  }

  private clearReconnect() {
    this.connectAttempts = 1;
    clearTimeout(this.reconnectTimer);
  }

  // calculate the next connection retry interval
  // exponential backoff with jitter
  private calcReconnectInterval(): number {
    let retryInterval: number = (Math.pow(2, this.connectAttempts) - 1) * 1000;

    if (retryInterval > this.maxReconnectInterval) {
      retryInterval = this.maxReconnectInterval;
    }

    // jitter is random number between +/- 10% of retry interval
    const jitter =
      Math.random() * retryInterval * 0.1 * (Math.random() >= 0.5 ? 1 : -1);

    return retryInterval + jitter;
  }

  private scheduleReconnect(
    namespace: string,
    directory: string,
    instanceId: string,
  ) {
    const reconnectMillis = this.calcReconnectInterval();

    this.logger.debug(`scheduleReconnect in ${reconnectMillis}ms`);

    this.reconnectTimer = window.setTimeout(() => {
      this.connectAttempts++;
      if (this.connectAttempts > this.maxReconnectAttempts) {
        this.clearReconnect();
        this.logger.error(
          'connection permanent failure, manual reconnection required',
        );
        this.handleDisconnected();
        return;
      }

      this.connect(namespace, directory, instanceId);
    }, reconnectMillis);

    return reconnectMillis;
  }

  private handleOpen() {
    this.logger.debug('connected to connectifi service');
    this.clearReconnect();
    this.connected = true;
    this.emit('connect', this.neverBeenConnected);
    this.neverBeenConnected = false;
  }

  private handleError() {
    this.logger.error('connectifi service error');
  }

  private handleMessage(event: MessageEvent) {
    const response: IframeResponse = JSON.parse(event.data);

    this.logger.debug('message received from connectifi service', response);

    switch (response.topic) {
      case 'message':
        return this.emit('message', JSON.parse(response.data));
      case 'close':
        return this.handleClose(response);
      case 'error':
        return this.handleError();
      case 'open':
        return this.handleOpen();
    }
  }

  private handleDisconnected(retryInMillis?: number) {
    this.connected = false;
    this.emit('disconnect', retryInMillis);
  }

  private handleClose({
    code,
    namespace,
    directory,
    instanceId,
  }: IframeCloseResponse) {
    this.logger.debug(
      `socket closed: ${code}, reconnect? ${this.autoReconnect}`,
    );

    if (this.autoReconnect) {
      const retryInMillis = this.scheduleReconnect(
        namespace,
        directory,
        instanceId,
      );
      this.handleDisconnected(retryInMillis);
    } else {
      this.handleDisconnected();
    }
  }

  private handleWindowBeforeUnload() {
    this.autoReconnect = false;
    closeWebSocket(this.port);
  }

  private addPendingEventId(eventId: string) {
    this.pendingEventIds.add(eventId);
    this.emit('working', true);
  }

  private removePendingEventId(eventId: string) {
    this.pendingEventIds.delete(eventId);
    if (!this.pendingEventIds.size) {
      this.emit('working', false);
    }
  }

  private addListeners() {
    if (this.hasAddedListeners) return;
    this.hasAddedListeners = true;

    this.port.addEventListener('message', this.handleMessage.bind(this));

    //close socket on window unload
    window.addEventListener(
      'beforeUnload',
      this.handleWindowBeforeUnload.bind(this),
    );
  }

  /** Connect WebSocket to interop server. */
  async connect(
    namespace: string,
    directory: string,
    instanceId: string,
  ): Promise<void> {
    this.addListeners();
    this.autoReconnect = true;

    this.logger.debug(
      `connecting to service, namespace: ${namespace}, directory: ${directory}, instance: ${instanceId}`,
    );

    return connectWebSocket(this.port, {
      namespace,
      directory,
      instanceId,
    });
  }

  /** Disconnect active websocket connection. */
  disconnect(): void {
    this.autoReconnect = false;
    this.clearReconnect();

    closeWebSocket(this.port);
  }

  send<TTopic extends UpTopic>(msg: UpMessage<TTopic>): void {
    this.logger.debug('sending message to connectifi service', msg);
    if (this.connected) {
      sendWebSocketMessage(this.port, msg);
    } else {
      throw new Error(ConnectionError.NoConnectionAvailable);
    }
  }

  async request<TTopic extends RoundTripTopic>(
    topic: TTopic,
    data: UpRequestData[TTopic],
    eventId = createEventId(topic),
  ) {
    const message: UpRequest<TTopic> = { topic, data, eventId };

    // Need cast here because of https://github.com/microsoft/TypeScript/issues/25879
    this.send(message as UpMessage<TTopic>);

    return new Promise<DownResponseData[TTopic]>((res, rej) => {
      const listener = (envelope: Envelope) => {
        if (eventId !== envelope.eventId) return;
        this.removePendingEventId(eventId);
        this.removeEventListener('message', listener);
        if (isErrorEnvelope(envelope)) {
          return rej(envelope.data.error);
        }
        res(envelope.data as DownResponseData[TTopic]);
      };
      this.addPendingEventId(eventId);
      this.addEventListener('message', listener);
    });
  }

  action<TTopic extends UpActionTopic>(
    topic: TTopic,
    data: UpActionData[TTopic],
  ): void {
    const message: UpAction<TTopic> = { topic, data };
    // Need cast here because of https://github.com/microsoft/TypeScript/issues/25879
    this.send(message as UpMessage<TTopic>);
  }

  isConnected() {
    return this.connected;
  }

  addEventListener(
    type: 'working',
    listener: (isWorking: boolean) => void,
  ): void;
  addEventListener(
    type: 'connect',
    listener: (hasNeverBeenConnected: boolean) => void,
  ): void;
  addEventListener(
    type: 'disconnect',
    listener: (retryInMillis: number) => void,
  ): void;
  addEventListener(type: 'message', listener: (event: Envelope) => void): void;
  override addEventListener(type: string, listener: (event: any) => void) {
    super.addEventListener(type, listener);
  }
}
